import {IAppError} from "@pg-mono/request-state";

import {IOfferDetailApplication} from "../../../../offer/types/IOfferDetailApplication";
import {IRecommendedOffer} from "../../../../offer/types/IRecommendedOffer";
import {IPropertyDetail} from "../../../../property/types/IPropertyDetail";
import {IPropertyDetailApplication} from "../../../../property/types/IPropertyDetailApplication";
import {IRecommendedProperty} from "../../../../property/types/IRecommendedProperty";
import {AmplitudeAuthPlacement} from "../../../../tracking/types/AmplitudeAuthPlacement";
import {IVendorDetailApplication} from "../../../../vendor/types/IVendorDetailApplication";
import {getAmplitudeInquirySource} from "../../../tracking/amplitude/get_amplitude_inquiry_source";
import {IAmplitudeLeadFormProductData} from "../../../tracking/amplitude/get_amplitude_lead_form_products";
import {
    trackAmplitudeLeadFormClose,
    trackAmplitudeLeadFormError,
    trackAmplitudeLeadFormGenerateLead,
    trackAmplitudeLeadFormStart
} from "../../../tracking/amplitude/track_amplitude_lead_form_events";
import {IApplicationData} from "../../../types/IApplicationData";
import {IAmplitudeLeadFormTrackingContext} from "../../../types/lead/IAmplitudeLeadFormEventTracking";
import {ApplicationMultiLeadRecommendationType} from "../../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationSource, ApplicationSourceSection} from "../../../utils/form/application_form_source";

export const CLASSIC_MULTI_LEAD_CTA_TEXT = "Zapytaj o oferty";
export const CLASSIC_MULTI_LEAD_LOCATION = "rp_multilead_post_lead";

type ClassicMultiLeadRecommendation = IRecommendedProperty | IPropertyDetail | IRecommendedOffer;

const isPropertyRecommendation = (recommendation: ClassicMultiLeadRecommendation): recommendation is IRecommendedProperty | IPropertyDetail =>
    "offer" in recommendation;

const getSource = (recommendationType: ApplicationMultiLeadRecommendationType) =>
    recommendationType === ApplicationMultiLeadRecommendationType.PROPERTY ? ApplicationSource.RecommendedProperty : ApplicationSource.RecommendedOffer;

const getLeadData = (recommendation: ClassicMultiLeadRecommendation): IAmplitudeLeadFormProductData => {
    if (isPropertyRecommendation(recommendation)) {
        return {
            vendor: {detail: {name: recommendation.offer.vendor.name} as unknown as IVendorDetailApplication},
            offer: {
                detail: {
                    id: recommendation.offer.id,
                    name: recommendation.offer.name,
                    type: recommendation.offer.type,
                    region: recommendation.offer.region,
                    construction_date_range: recommendation.offer.construction_date_range
                } as unknown as IOfferDetailApplication
            },
            property: {
                detail: {
                    id: recommendation.id,
                    number: recommendation.number,
                    area: recommendation.area,
                    rooms: recommendation.rooms,
                    floor: recommendation.floor,
                    price: recommendation.price
                } as unknown as IPropertyDetailApplication
            }
        };
    }

    return {
        vendor: {detail: {name: recommendation.vendor.name} as unknown as IVendorDetailApplication},
        offer: {
            detail: {
                id: recommendation.id,
                name: recommendation.name,
                type: recommendation.type,
                region: recommendation.region
            } as unknown as IOfferDetailApplication
        }
    };
};

const getTrackingContext = (
    recommendation: ClassicMultiLeadRecommendation,
    source: ApplicationSource,
    authPlacement: AmplitudeAuthPlacement | null | undefined
): IAmplitudeLeadFormTrackingContext => ({
    inquiry_source: getAmplitudeInquirySource(source),
    cta_text: CLASSIC_MULTI_LEAD_CTA_TEXT,
    form_location: CLASSIC_MULTI_LEAD_LOCATION,
    source_id: source,
    lead_type: "multi_lead",
    item_id: isPropertyRecommendation(recommendation) ? recommendation.offer.id : recommendation.id,
    ...(isPropertyRecommendation(recommendation) ? {property_id: recommendation.id} : {}),
    ...(authPlacement !== undefined && {auth_placement: authPlacement})
});

export const trackClassicMultiLeadStart = (
    recommendationType: ApplicationMultiLeadRecommendationType,
    recommendations: ClassicMultiLeadRecommendation[],
    authPlacement?: AmplitudeAuthPlacement | null
) => {
    const firstRecommendation = recommendations[0];
    if (!firstRecommendation) {
        return;
    }

    const source = getSource(recommendationType);
    trackAmplitudeLeadFormStart({
        trackingContext: getTrackingContext(firstRecommendation, source, authPlacement),
        leadData: recommendations.map(getLeadData),
        source,
        sourceSection: ApplicationSourceSection.MULTILEAD,
        eventAction: "autofill"
    });
};

export const trackClassicMultiLeadClose = (
    recommendationType: ApplicationMultiLeadRecommendationType,
    recommendations: ClassicMultiLeadRecommendation[],
    authPlacement?: AmplitudeAuthPlacement | null
) => {
    const firstRecommendation = recommendations[0];
    if (!firstRecommendation) {
        return;
    }

    const source = getSource(recommendationType);
    trackAmplitudeLeadFormClose({
        trackingContext: getTrackingContext(firstRecommendation, source, authPlacement),
        leadData: recommendations.map(getLeadData),
        source,
        sourceSection: ApplicationSourceSection.MULTILEAD,
        eventAction: "autofill"
    });
};

export const trackClassicMultiLeadError = ({
    recommendationType,
    recommendations,
    appError,
    errorCode,
    authPlacement
}: {
    recommendationType: ApplicationMultiLeadRecommendationType;
    recommendations: ClassicMultiLeadRecommendation[];
    appError: IAppError;
    errorCode?: string;
    authPlacement?: AmplitudeAuthPlacement | null;
}) => {
    const firstRecommendation = recommendations[0];
    if (!firstRecommendation) {
        return;
    }

    const source = getSource(recommendationType);
    trackAmplitudeLeadFormError({
        trackingContext: getTrackingContext(firstRecommendation, source, authPlacement),
        leadData: recommendations.map(getLeadData),
        source,
        sourceSection: ApplicationSourceSection.MULTILEAD,
        appError,
        ...(errorCode ? {errorCode} : {})
    });
};

export const trackClassicMultiLeadGenerate = ({
    recommendationType,
    createdApplications,
    authPlacement
}: {
    recommendationType: ApplicationMultiLeadRecommendationType;
    createdApplications: [IApplicationData, ClassicMultiLeadRecommendation][];
    authPlacement?: AmplitudeAuthPlacement | null;
}) => {
    const [firstCreatedApplication, firstRecommendation] = createdApplications[0] ?? [];
    if (!firstCreatedApplication || !firstRecommendation) {
        return;
    }

    const source = getSource(recommendationType);
    const leadData = createdApplications.map(([application, recommendation]) => ({
        ...getLeadData(recommendation),
        transactionId: application.uuid
    }));

    trackAmplitudeLeadFormGenerateLead({
        trackingContext: getTrackingContext(firstRecommendation, source, authPlacement),
        leadData,
        source,
        sourceSection: ApplicationSourceSection.MULTILEAD,
        eventAction: "autofill",
        marketingApproval: Boolean(firstCreatedApplication.marketing_email || firstCreatedApplication.marketing_sms),
        inquirySelection: {additionalQuestions: []}
    });
};
import {IAppError} from "@pg-mono/request-state";

import {IOfferDetailApplication} from "../../../../offer/types/IOfferDetailApplication";
import {IRecommendedOffer} from "../../../../offer/types/IRecommendedOffer";
import {IPropertyDetail} from "../../../../property/types/IPropertyDetail";
import {IPropertyDetailApplication} from "../../../../property/types/IPropertyDetailApplication";
import {IRecommendedProperty} from "../../../../property/types/IRecommendedProperty";
import {AmplitudeAuthPlacement} from "../../../../tracking/types/AmplitudeAuthPlacement";
import {IVendorDetailApplication} from "../../../../vendor/types/IVendorDetailApplication";
import {getAmplitudeInquirySource} from "../../../tracking/amplitude/get_amplitude_inquiry_source";
import {IAmplitudeLeadFormProductData} from "../../../tracking/amplitude/get_amplitude_lead_form_products";
import {
    trackAmplitudeLeadFormClose,
    trackAmplitudeLeadFormError,
    trackAmplitudeLeadFormGenerateLead,
    trackAmplitudeLeadFormStart
} from "../../../tracking/amplitude/track_amplitude_lead_form_events";
import {IApplicationData} from "../../../types/IApplicationData";
import {IAmplitudeLeadFormTrackingContext} from "../../../types/lead/IAmplitudeLeadFormEventTracking";
import {ApplicationMultiLeadRecommendationType} from "../../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationSource, ApplicationSourceSection} from "../../../utils/form/application_form_source";

export const CLASSIC_MULTI_LEAD_CTA_TEXT = "Zapytaj o oferty";
export const CLASSIC_MULTI_LEAD_LOCATION = "rp_multilead_post_lead";

type ClassicMultiLeadRecommendation = IRecommendedProperty | IPropertyDetail | IRecommendedOffer;

const isPropertyRecommendation = (recommendation: ClassicMultiLeadRecommendation): recommendation is IRecommendedProperty | IPropertyDetail =>
    "offer" in recommendation;

const getSource = (recommendationType: ApplicationMultiLeadRecommendationType) =>
    recommendationType === ApplicationMultiLeadRecommendationType.PROPERTY ? ApplicationSource.RecommendedProperty : ApplicationSource.RecommendedOffer;

const getLeadData = (recommendation: ClassicMultiLeadRecommendation): IAmplitudeLeadFormProductData => {
    if (isPropertyRecommendation(recommendation)) {
        return {
            vendor: {detail: {name: recommendation.offer.vendor.name} as unknown as IVendorDetailApplication},
            offer: {
                detail: {
                    id: recommendation.offer.id,
                    name: recommendation.offer.name,
                    type: recommendation.offer.type,
                    region: recommendation.offer.region,
                    construction_date_range: recommendation.offer.construction_date_range
                } as unknown as IOfferDetailApplication
            },
            property: {
                detail: {
                    id: recommendation.id,
                    number: recommendation.number,
                    area: recommendation.area,
                    rooms: recommendation.rooms,
                    floor: recommendation.floor,
                    price: recommendation.price
                } as unknown as IPropertyDetailApplication
            }
        };
    }

    return {
        vendor: {detail: {name: recommendation.vendor.name} as unknown as IVendorDetailApplication},
        offer: {
            detail: {
                id: recommendation.id,
                name: recommendation.name,
                type: recommendation.type,
                region: recommendation.region
            } as unknown as IOfferDetailApplication
        }
    };
};

const getTrackingContext = (
    recommendation: ClassicMultiLeadRecommendation,
    source: ApplicationSource,
    authPlacement: AmplitudeAuthPlacement | null | undefined
): IAmplitudeLeadFormTrackingContext => ({
    inquiry_source: getAmplitudeInquirySource(source),
    cta_text: CLASSIC_MULTI_LEAD_CTA_TEXT,
    form_location: CLASSIC_MULTI_LEAD_LOCATION,
    source_id: source,
    lead_type: "multi_lead",
    item_id: isPropertyRecommendation(recommendation) ? recommendation.offer.id : recommendation.id,
    ...(isPropertyRecommendation(recommendation) ? {property_id: recommendation.id} : {}),
    ...(authPlacement !== undefined && {auth_placement: authPlacement})
});

export const trackClassicMultiLeadStart = (
    recommendationType: ApplicationMultiLeadRecommendationType,
    recommendations: ClassicMultiLeadRecommendation[],
    authPlacement?: AmplitudeAuthPlacement | null
) => {
    const firstRecommendation = recommendations[0];
    if (!firstRecommendation) {
        return;
    }

    const source = getSource(recommendationType);
    trackAmplitudeLeadFormStart({
        trackingContext: getTrackingContext(firstRecommendation, source, authPlacement),
        leadData: recommendations.map(getLeadData),
        source,
        sourceSection: ApplicationSourceSection.MULTILEAD,
        eventAction: "autofill"
    });
};

export const trackClassicMultiLeadClose = (
    recommendationType: ApplicationMultiLeadRecommendationType,
    recommendations: ClassicMultiLeadRecommendation[],
    authPlacement?: AmplitudeAuthPlacement | null
) => {
    const firstRecommendation = recommendations[0];
    if (!firstRecommendation) {
        return;
    }

    const source = getSource(recommendationType);
    trackAmplitudeLeadFormClose({
        trackingContext: getTrackingContext(firstRecommendation, source, authPlacement),
        leadData: recommendations.map(getLeadData),
        source,
        sourceSection: ApplicationSourceSection.MULTILEAD,
        eventAction: "autofill"
    });
};

export const trackClassicMultiLeadError = ({
    recommendationType,
    recommendations,
    appError,
    errorCode,
    authPlacement
}: {
    recommendationType: ApplicationMultiLeadRecommendationType;
    recommendations: ClassicMultiLeadRecommendation[];
    appError: IAppError;
    errorCode?: string;
    authPlacement?: AmplitudeAuthPlacement | null;
}) => {
    const firstRecommendation = recommendations[0];
    if (!firstRecommendation) {
        return;
    }

    const source = getSource(recommendationType);
    trackAmplitudeLeadFormError({
        trackingContext: getTrackingContext(firstRecommendation, source, authPlacement),
        leadData: recommendations.map(getLeadData),
        source,
        sourceSection: ApplicationSourceSection.MULTILEAD,
        appError,
        ...(errorCode ? {errorCode} : {})
    });
};

export const trackClassicMultiLeadGenerate = ({
    recommendationType,
    createdApplications,
    authPlacement
}: {
    recommendationType: ApplicationMultiLeadRecommendationType;
    createdApplications: [IApplicationData, ClassicMultiLeadRecommendation][];
    authPlacement?: AmplitudeAuthPlacement | null;
}) => {
    const [firstCreatedApplication, firstRecommendation] = createdApplications[0] ?? [];
    if (!firstCreatedApplication || !firstRecommendation) {
        return;
    }

    const source = getSource(recommendationType);
    const leadData = createdApplications.map(([application, recommendation]) => ({
        ...getLeadData(recommendation),
        transactionId: application.uuid
    }));

    trackAmplitudeLeadFormGenerateLead({
        trackingContext: getTrackingContext(firstRecommendation, source, authPlacement),
        leadData,
        source,
        sourceSection: ApplicationSourceSection.MULTILEAD,
        eventAction: "autofill",
        marketingApproval: Boolean(firstCreatedApplication.marketing_email || firstCreatedApplication.marketing_sms),
        inquirySelection: {additionalQuestions: []}
    });
};
